import { generateSupportLoginLink, copyToClipboard } from '@choco-admin/utils';
import { Button, Checkbox, Link, Tooltip } from '@heroui/react';
import React, { useCallback, useMemo } from 'react';
import { FiCopy, FiInfo } from 'react-icons/fi';

import { useNotification } from '../../../../contexts/NotificationContext';

export interface SupportLoginProps {
  /** Whether support login is enabled */
  isEnabled: boolean;
  /** Callback when support login state changes */
  onToggle: (enabled: boolean) => void;
  /** User's phone number for generating the support login link */
  phoneNumber?: string;
  /** User's name for display */
  userName?: string;
  /** Whether the checkbox should be disabled */
  disabled?: boolean;
  /** Whether this is only available for phone login method */
  isPhoneLoginOnly?: boolean;
}

export const SupportLogin: React.FC<SupportLoginProps> = ({
  isEnabled,
  onToggle,
  phoneNumber,
  userName,
  disabled = false,
  isPhoneLoginOnly = false,
}) => {
  const { addNotification } = useNotification();

  // Generate support login link
  const supportLoginLink = useMemo(() => {
    if (!phoneNumber) return '';
    return generateSupportLoginLink({
      phoneNumber,
      environment: 'auto', // Let the utility auto-detect the correct environment
    });
  }, [phoneNumber]);

  const handleCopyLink = useCallback(async () => {
    if (!supportLoginLink) return;

    const success = await copyToClipboard(supportLoginLink);

    if (success) {
      addNotification({
        title: 'Link Copied',
        message:
          "You are good at copying. Don't forget to paste what you copied 🤓",
        type: 'success',
      });
    } else {
      addNotification({
        title: 'Copy Failed',
        message: 'You are good at copying but we failed you.',
        type: 'error',
      });
    }
  }, [supportLoginLink, addNotification]);

  const handleOpenLink = useCallback(() => {
    if (!supportLoginLink) return;
    window.open(supportLoginLink, '_blank', 'noopener,noreferrer');
  }, [supportLoginLink]);

  const loginAsText = `Login as ${userName || 'User'}`;

  const tooltipContent = (
    <div className="max-w-sm space-y-3 p-2">
      <p className="text-sm">
        Make sure to log in with this link when you log into an external user's
        account. Only then we can exclude tracking on internal users from our
        analytics to keep it unbiased.
      </p>
      <div className="p-2 bg-danger-50 border border-danger-200 rounded-md">
        <p className="text-xs text-danger-700">
          Active support login is indicated in red.
        </p>
      </div>
      <p className="text-sm">
        On a device that has the App installed the link opens the Choco App,
        otherwise, it opens Choco Web.
      </p>
      <p className="text-sm">
        <strong className="text-warning-600">Always remember</strong> to
        unselect this option when you are done. Otherwise, the user will not be
        able to log in anymore, as he will not receive a code.
      </p>
    </div>
  );

  const isDisabled = disabled || !phoneNumber || isPhoneLoginOnly;
  const showWarning = isPhoneLoginOnly && !disabled;

  return (
    <>
      <Checkbox
        isSelected={isEnabled}
        onValueChange={onToggle}
        isDisabled={isDisabled || showWarning}
        size="sm"
        classNames={{
          label: `text-foreground ${
            isEnabled ? 'font-medium text-danger-600' : ''
          } flex items-center pl-2`,
          base: 'max-w-none',
          hiddenInput: 'h-6',
        }}
      >
        <span>Support login (daily code)</span>
        <Tooltip
          content={tooltipContent}
          placement="top"
          className="max-w-none"
          classNames={{
            content: 'bg-content1 border border-default-200 shadow-lg',
          }}
        >
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className="min-w-6 w-4 h-4"
          >
            <FiInfo className="h-3 w-3 text-default-500" />
          </Button>
        </Tooltip>
      </Checkbox>

      {showWarning && (
        <div className="text-xs text-warning-600 pl-6 mt-2">
          Support login is only available for 'Phone/SMS' login method.
        </div>
      )}

      {isEnabled && supportLoginLink && (
        <div className="flex items-center pl-8">
          <Link
            href={supportLoginLink}
            isExternal
            showAnchorIcon
            className="text-xs text-primary hover:text-primary-700 underline hover:no-underline"
            onPress={handleOpenLink}
          >
            {loginAsText}
          </Link>
          <Button
            isIconOnly
            size="sm"
            variant="flat"
            color="primary"
            onPress={handleCopyLink}
            className="min-w-6 w-6 h-6"
            aria-label="Copy support login link"
          >
            <FiCopy className="h-3 w-3" />
          </Button>
        </div>
      )}
    </>
  );
};
